// copied from VPC, edited

import {
  buildColorAccessor,
  buildMeasureAccessor,
  computeMetricComparison,
  DataPointsApiResponse,
  DataRow,
  getLatestValue,
  getMeasureFormatString,
  MetricBlue,
  MetricsNeutralColor,
} from '@tableau/metrics-engine';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import { connect } from 'react-redux';

import { CommonStyles } from '../../../common/styles/CommonStyles';

import { AppState } from '../../../app/state/AppState';
import { isMetricInfo } from '../../models/IMetricInfo';
import { selectMetricDataForContentItem } from '../../selectors/selectMetrics';
import { SearchResultItem, Workbook } from '../../models/SearchResultTypes';
import { isMetricsEnabled } from '../../utils/MetricUtils';
import { MetricValuePill } from '@tableau/metrics-shared-widgets-dom';
import { FeatureFlag, FeatureFlagService } from '../../services/FeatureFlagService';
import { getMetricMeasure, getTableWithDefaultComparison } from './metricCardPropsFromDataPoints';

const containerCellStyle: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  padding: '0 12px',
  paddingTop: CommonStyles.Spacing.MediumSpace,
};

export interface MetricValuePillWrapperStateProps {
  readonly metricData?: DataPointsApiResponse;
  readonly measureAccessor?: (point: DataRow) => number;
  readonly colorAccessor?: (point: DataRow) => string;
}

export interface MetricValuePillWrapperOwnProps {
  readonly row: DataGrid.Row;
}

export type MetricValuePillWrapperProps = MetricValuePillWrapperStateProps & MetricValuePillWrapperOwnProps;

export class MetricValuePillWrapper extends React.PureComponent<MetricValuePillWrapperProps> {
  public render() {
    const rowData = this.props.row.data as SearchResultItem;

    const testId = this.props.row.data ? `metric-value-cell-${this.props.row.data.id}` : 'metric-value-cell';
    if (!isMetricInfo(rowData) || !isMetricsEnabled()) {
      // render blank cell for non-metrics. This happens in mixed content pages.
      return null;
    }

    let measure;
    let format;

    if (this.props.metricData && this.props.metricData.data && this.props.metricData.data.length > 0) {
      measure = this.props.measureAccessor && getMetricMeasure(this.props.metricData, this.props.measureAccessor);
      format = this.props.metricData && getMeasureFormatString(this.props.metricData.definition);
    }

    const comparisonTable = this.props.metricData && getTableWithDefaultComparison(this.props.metricData);

    const latestValue = comparisonTable && getLatestValue(comparisonTable);
    const comparison = latestValue && computeMetricComparison(latestValue);

    const colorsEnabledColor =
      latestValue && comparison && this.props.colorAccessor ? this.props.colorAccessor(comparison.comparedPoint) : MetricsNeutralColor;

    const color = FeatureFlagService.instance.getValue(FeatureFlag.metricsColorIndicatorsEnabled) ? colorsEnabledColor : MetricBlue;

    return (
      <span style={(CommonStyles.Typography.defaultFont, containerCellStyle)} data-tb-test-id={testId}>
        <MetricValuePill
          value={measure}
          measureFormat={format}
          color={color}
          colorIndicatorsEnabled={FeatureFlagService.instance.getValue(FeatureFlag.metricsColorIndicatorsEnabled)}
          leftAlign={true}
        />
      </span>
    );
  }
}

const mapStateToProps = (state: AppState, ownProps: MetricValuePillWrapperOwnProps) => {
  const metricData = ownProps.row.data ? selectMetricDataForContentItem(state, ownProps.row.data as Workbook) : undefined;

  return {
    metricData,
    measureAccessor: metricData ? buildMeasureAccessor(metricData.definition) : undefined,
    colorAccessor: metricData ? buildColorAccessor(metricData.definition) : undefined,
  };
};

export const ConnectedMetricValuePillWrapper = connect(mapStateToProps)(MetricValuePillWrapper);
